import { useTranslation } from 'react-i18next'
import {
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Legend,
    CartesianGrid,
} from 'recharts'
import { CHART_COLORS, CHART_THEME } from '@/constants/chartColors'
import { QuarterlyMetrics } from '@/modules/Survey/types/quarterly-report'
import { useLegendToggle } from '../hooks/use-legend-toggle'

interface CustomerFlowChartProps {
    data: QuarterlyMetrics[]
}

export const CustomerFlowChart = ({ data }: CustomerFlowChartProps) => {
    const { t } = useTranslation('surveys')
    const { handleLegendClick, isHidden } = useLegendToggle()

    const newLabel = t('surveys.quarterly_report.new')
    const churnedLabel = t('surveys.quarterly_report.churned')
    const activeLabel = t('surveys.quarterly_report.active_customers')

    const chartData = data.map((row) => ({
        quarter: row.quarter,
        [newLabel]: row.new_customers,
        [churnedLabel]: row.churned_customers,
        [activeLabel]: row.active_customers_end,
    }))

    return (
        <div className="bg-white rounded-lg p-4 border border-gray-100">
            <h3 className="font-semibold text-gray-700 mb-4">
                {t('surveys.quarterly_report.chart_customer_flow')} —{' '}
                {t('surveys.quarterly_report.company_wide')}
            </h3>
            <ResponsiveContainer width="100%" height={320}>
                <ComposedChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                    <CartesianGrid {...CHART_THEME.grid} />
                    <XAxis
                        dataKey="quarter"
                        {...CHART_THEME.axis}
                        tickLine={false}
                    />
                    <YAxis yAxisId="bar" {...CHART_THEME.axis} />
                    <YAxis
                        yAxisId="line"
                        orientation="right"
                        {...CHART_THEME.axis}
                    />
                    <Tooltip contentStyle={CHART_THEME.tooltip.contentStyle} />
                    <Legend
                        wrapperStyle={{
                            fontSize: '12px',
                            paddingTop: '8px',
                            cursor: 'pointer',
                        }}
                        iconSize={10}
                        onClick={handleLegendClick}
                    />
                    <Bar
                        yAxisId="bar"
                        dataKey={newLabel}
                        fill={CHART_COLORS.EMERALD_GREEN}
                        radius={[3, 3, 0, 0]}
                        hide={isHidden(newLabel)}
                    />
                    <Bar
                        yAxisId="bar"
                        dataKey={churnedLabel}
                        fill={CHART_COLORS.ROSE_RED}
                        radius={[3, 3, 0, 0]}
                        hide={isHidden(churnedLabel)}
                    />
                    <Line
                        yAxisId="line"
                        type="monotone"
                        dataKey={activeLabel}
                        stroke={CHART_COLORS.PRIMARY_BLUE}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        hide={isHidden(activeLabel)}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    )
}
